import type { Envelope } from './types'

export const Code = {
  BadRequest: 400,
  Unauthorized: 401,
  Forbidden: 403,
  NotFound: 404,
  Conflict: 409,
  Validation: 422,
  TooManyRequests: 429,
  Internal: 500,
  Unavailable: 503,

  BadCredentials: 10001,
  CaptchaRequired: 10002,
  LoginLocked: 10003,
  UserDisabled: 10004,
  TenantDisabled: 10005,
  WeakPassword: 10006,
  OldPasswordWrong: 10007,
  RefreshInvalid: 10008,

  AppNotOpened: 40301,
  MustChangePwd: 40302,
  TenantInactive: 40303,

  TenantCodeTaken: 10101,
  UsernameTaken: 10102,
  RoleCodeTaken: 10103,
  DeptNotEmpty: 10104,
  RoleInUse: 10105,
  BuiltinRole: 10106,
  InvalidPerms: 10107,
  DeptCycle: 10108,
  OperatorTenant: 10109,
  SelfDisable: 10110,
  LastAdmin: 10111,
} as const

export type CodeValue = (typeof Code)[keyof typeof Code]

export class YcError extends Error {
  readonly code: number
  readonly httpStatus: number
  readonly data: unknown
  readonly traceId: string

  constructor(opts: { code: number; msg: string; httpStatus?: number; data?: unknown; traceId?: string }) {
    super(opts.msg)
    this.name = 'YcError'
    this.code = opts.code
    this.httpStatus = opts.httpStatus ?? 0
    this.data = opts.data
    this.traceId = opts.traceId ?? ''
  }

  /** 面向用户的一行提示，含 trace_id 便于对日志。 */
  get display(): string {
    return this.traceId ? `${this.message}（trace_id: ${this.traceId}）` : this.message
  }
}

export function isYcError(e: unknown): e is YcError {
  return e instanceof YcError
}

export function hasCode(e: unknown, code: number): boolean {
  return isYcError(e) && e.code === code
}

export function networkError(cause?: unknown): YcError {
  const msg = cause instanceof Error && cause.message ? cause.message : '网络异常'
  return new YcError({ code: 0, msg: `网络异常：${msg}`, httpStatus: 0 })
}

export function toYcError(body: unknown, httpStatus: number): YcError {
  const env = body as Partial<Envelope<unknown>> | undefined
  if (env && typeof env.code === 'number') {
    return new YcError({
      code: env.code,
      msg: env.msg ?? '请求失败',
      httpStatus,
      data: env.data,
      traceId: env.trace_id ?? '',
    })
  }
  return new YcError({ code: httpStatus, msg: `请求失败（HTTP ${httpStatus}）`, httpStatus })
}

export const isUnauthorized = (e: unknown): boolean => hasCode(e, Code.Unauthorized)

export const captchaRequired = (e: unknown): boolean => hasCode(e, Code.CaptchaRequired)

export const mustChangePwd = (e: unknown): boolean => hasCode(e, Code.MustChangePwd)

export const appNotOpened = (e: unknown): boolean => hasCode(e, Code.AppNotOpened)

export const tenantInactive = (e: unknown): boolean => hasCode(e, Code.TenantInactive)

export const userDisabled = (e: unknown): boolean => hasCode(e, Code.UserDisabled)

export const tenantDisabled = (e: unknown): boolean => hasCode(e, Code.TenantDisabled)

export const loginLocked = (e: unknown): boolean => hasCode(e, Code.LoginLocked)

export const rateLimited = (e: unknown): boolean => hasCode(e, Code.TooManyRequests)

export function invalidPerms(e: unknown): string[] {
  if (!hasCode(e, Code.InvalidPerms)) return []
  const data = (e as YcError).data as { invalid?: unknown } | undefined
  const list = data?.invalid
  return Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : []
}